import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { getInsight } from "@/lib/insights";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });

export default function InsightDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/insights/${slug}`);
        if (mounted) setItem(data);
      } catch (e) {
        if (mounted) setError("Article not found.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <main className="pt-32 pb-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-sm text-[#06102A]/60">Loading…</div>
      </main>
    );
  }

  if (error || !item) {
    return (
      <main className="pt-32 pb-24 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-sm text-[#06102A]/60">{error || "Article not found."}</p>
          <Link
            to="/insights"
            className="mt-6 inline-flex items-center gap-2 text-sm text-[#06102A] vt-link"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Insights
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-white" data-testid="insight-detail-page">
      <section className="pt-32 md:pt-40 pb-12">
        <div className="max-w-3xl mx-auto px-6">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-sm text-[#06102A]/65 hover:text-[#06102A] transition-colors"
            data-testid="insight-back"
          >
            <ArrowLeft className="h-4 w-4" /> Insights
          </Link>
          <Reveal>
            <p className="mt-8 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#F97316]">
              {item.category}
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#06102A]">
              {item.title}
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-6 flex items-center gap-4 text-xs text-[#06102A]/60">
              <span>{item.author}</span>
              <span>·</span>
              <span>{fmtDate(item.published_at)}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {item.read_minutes} min read
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {item.image_url && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6">
            <Reveal delay={120}>
              <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-[#06102A]/5 border border-[#06102A]/10">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <article className="pb-24">
        <div className="max-w-3xl mx-auto px-6 prose-lg">
          {item.body.split(/\n\n+/).map((p, i) => (
            <p
              key={i}
              className="mt-6 text-[17px] leading-[1.75] text-[#06102A]/85"
              data-testid={`insight-paragraph-${i}`}
            >
              {p}
            </p>
          ))}
        </div>
      </article>

      <section className="py-20 bg-[#FAFBFC] border-t border-[#06102A]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-display text-3xl md:text-4xl font-medium text-[#06102A]">
            See Vaultavo in action.
          </h3>
          <Link
            to="/contact"
            data-testid="insight-cta-demo"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all"
          >
            Request a Demo <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
